import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useCallback } from 'react';

import { AuthQueryOptions, VERIFICATION_TYPE } from '@/api';
import { ROUTES } from '@/constants';

import { useCustomRouter } from './useCustomRouter';

interface UseVerificationCheckOptions {
  /**
   * 쿼리 활성화 여부
   * @default true
   */
  enabled?: boolean;
  /**
   * 본인인증 페이지 리다이렉트 후 돌아올 경로
   * @default 현재 pathname
   */
  redirectPath?: string;
}

/**
 * 사용자의 본인인증 상태를 확인하고, 본인인증이 필요한 경우 본인인증 페이지로 리다이렉트하는 함수를 제공하는 훅
 *
 * @example
 * ```tsx
 * const { checkVerification, isCi } = useVerificationCheck();
 *
 * const handleClick = () => {
 *   // 본인인증이 안되어 있으면 본인인증 페이지로 리다이렉트하고 true 반환
 *   // 본인인증이 되어있으면 false 반환
 *   if (checkVerification()) {
 *     return; // 본인인증 페이지로 이동했으므로 종료
 *   }
 *
 *   // 본인인증이 되어있으면 다음 로직 실행
 *   doSomething();
 * };
 * ```
 */
export const useVerificationCheck = (
  options: UseVerificationCheckOptions = {},
) => {
  const { enabled = true, redirectPath } = options;

  const pathname = usePathname();
  const router = useCustomRouter();

  // 본인인증 정보 조회
  const verificationInfoQuery = useQuery({
    ...AuthQueryOptions['post:/wello/v2/auth/member-verification/info'](),
    enabled,
  });

  const isCi = !!verificationInfoQuery.data?.context?.ci;

  const redirectToVerification = useCallback(() => {
    router.push(
      ROUTES.EASY_CERTIFICATION.withSearchParams({
        searchParams: {
          verification: VERIFICATION_TYPE.PROFILE_UPDATE,
          redirect: redirectPath ?? pathname ?? '',
        },
      }),
    );
  }, [router, redirectPath, pathname]);

  /**
   * 본인인증 상태를 체크하고, 본인인증이 필요한 경우 본인인증 페이지로 리다이렉트
   * @returns 본인인증 페이지로 리다이렉트했으면 true, 본인인증이 되어있으면 false
   */
  const checkVerification = useCallback(() => {
    if (!isCi) {
      redirectToVerification();

      return true;
    }

    return false;
  }, [isCi, redirectToVerification]);

  return {
    /** 본인인증 상태를 체크하고 필요 시 리다이렉트하는 함수 */
    checkVerification,
    /** ci 여부와 무관하게 본인인증 페이지로 보낸다 — identityKey 미발급처럼 ci만으로 판정할 수 없는 경우용 */
    redirectToVerification,
    /** 본인인증이 완료되었는지 여부 */
    isCi,
    /** 본인인증 정보 로딩 중인지 여부 */
    isLoading: verificationInfoQuery.isLoading,
  };
};
